/* ============================================================================
   The caffeine quiz.

     <unc-quiz>   five questions, a score, one of four tiers

   Loaded only on the page whose template suffix is `quiz`.

   EVERYTHING IS SERVER-RENDERED. Every question and every tier's result block is
   in the DOM before this runs; this element only decides which one is visible.
   That is the same arrangement as <unc-sc-mech> on the Science page, and it is
   deliberate: the copy is the point of the page, so it has to be crawlable and
   it has to survive JS failing. With JS off you get question one and all four
   tiers, which is verbose but readable, rather than a blank screen.

   STATE LIVES IN THE DOM. `data-qz-answer` on each question holds the chosen
   score, `data-tier` on the host names the matched tier, and CSS attribute
   selectors do the rest. There is no parallel state object to fall out of sync.
   ============================================================================ */
(function () {
  'use strict';

  var FOCUSABLE = 'button:not([disabled]), a[href], input, [tabindex]:not([tabindex="-1"])';

  if (customElements.get('unc-quiz')) return;

  customElements.define(
    'unc-quiz',
    class UncQuiz extends HTMLElement {
      connectedCallback() {
        this.questions = Array.from(this.querySelectorAll('[data-qz-q]'));
        this.results = Array.from(this.querySelectorAll('[data-qz-tier]'));
        if (!this.questions.length || !this.results.length) return;

        this.screens = {
          intro: this.querySelector('[data-qz-screen="intro"]'),
          quiz: this.querySelector('[data-qz-screen="quiz"]'),
          results: this.querySelector('[data-qz-screen="results"]')
        };

        this.bar = this.querySelector('[data-qz-bar]');
        this.stepNow = this.querySelector('[data-qz-step]');
        this.stepAll = this.querySelector('[data-qz-total]');
        this.backBtn = this.querySelector('[data-qz-back]');
        this.scoreOut = this.querySelector('[data-qz-score]');
        this.tagsInput = this.querySelector('[data-qz-tags]');
        this.noteInput = this.querySelector('[data-qz-note]');

        if (this.stepAll) this.stepAll.textContent = String(this.questions.length);

        this.at = 0;
        this.key = 'unc-quiz-' + this.id;
        this.onClick = this.onClick.bind(this);
        this.addEventListener('click', this.onClick);

        /* THE FORM POST RELOADS THE PAGE, so the result has to survive it.

           The email form is a normal POST to /contact, which is the whole point:
           the previous AJAX version treated Shopify's 200-with-errors response
           as success and unlocked results whatever was typed. The cost of a real
           POST is a full page load, and without this the visitor came back to a
           freshly constructed element sitting on the intro screen, having lost
           the result they had just filled a form to keep.

           So the answers go into sessionStorage when the quiz finishes, and if
           we come back from a submission we replay them and land on the results
           with the form's success message showing in place of the fields.

           Shopify redirects to ?customer_posted=true. The success paragraph is
           also checked, because a merchant may change the return URL. */
        var posted =
          window.location.search.indexOf('customer_posted=true') > -1 ||
          !!this.querySelector('.unc-qz__msg:not(.unc-qz__msg--error)');

        /* A rejected email comes back from the POST too, with the error
           paragraph rendered inside the results screen. Landing on the
           intro in that case hid the one message explaining what went
           wrong, and the visitor had to retake the quiz to try again. */
        var errored = !!this.querySelector('.unc-qz__msg--error');

        if ((posted || errored) && this.restore()) return;

        this.showScreen('intro');
      }

      /* Replay the stored answers and jump straight to the results. Returns
         false if there is nothing usable, so the caller falls back to intro. */
      restore() {
        var raw;
        try {
          raw = window.sessionStorage.getItem(this.key);
        } catch (e) {
          return false;
        }
        if (!raw) return false;

        var saved;
        try {
          saved = JSON.parse(raw);
        } catch (e) {
          return false;
        }
        if (!saved || !saved.length || saved.length !== this.questions.length) return false;

        var ok = true;
        this.questions.forEach(function (q, i) {
          var btn = q.querySelector('[data-qz-score-value="' + saved[i] + '"]');
          if (!btn) {
            ok = false;
            return;
          }
          q.dataset.qzAnswer = btn.dataset.qzScoreValue;
          q.dataset.qzAnswerLabel = btn.dataset.qzLabel || '';
          btn.setAttribute('aria-pressed', 'true');
        });
        if (!ok) return false;

        this.finish();
        return true;
      }

      save() {
        try {
          var picked = this.questions.map(function (q) {
            return q.dataset.qzAnswer || '';
          });
          window.sessionStorage.setItem(this.key, JSON.stringify(picked));
        } catch (e) {
          /* Private mode, or storage full. The quiz still works; only the
             restore after submitting the form is lost. */
        }
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
      }

      onClick(e) {
        var unlock = e.target.closest('[data-qz-unlock]');
        if (unlock && this.contains(unlock)) {
          this.jumpToGate();
          return;
        }

        var start = e.target.closest('[data-qz-start]');
        if (start) {
          e.preventDefault();
          this.showScreen('quiz');
          this.goTo(0);
          return;
        }

        var back = e.target.closest('[data-qz-back]');
        if (back) {
          e.preventDefault();
          this.back();
          return;
        }

        var again = e.target.closest('[data-qz-restart]');
        if (again) {
          e.preventDefault();
          this.reset();
          return;
        }

        var opt = e.target.closest('[data-qz-score-value]');
        if (opt && this.contains(opt)) {
          e.preventDefault();
          this.answer(opt);
        }
      }

      /* ----------------------------------------------------------------------
         answering
      ---------------------------------------------------------------------- */
      answer(opt) {
        var q = opt.closest('[data-qz-q]');
        if (!q) return;

        q.dataset.qzAnswer = opt.dataset.qzScoreValue;
        q.dataset.qzAnswerLabel = opt.dataset.qzLabel || '';

        Array.from(q.querySelectorAll('[data-qz-score-value]')).forEach(function (b) {
          b.setAttribute('aria-pressed', b === opt ? 'true' : 'false');
        });

        /* A SHORT PAUSE, THEN ADVANCE, and the buttons stay enabled.

           The previous quiz disabled every option on click and auto-advanced,
           which meant a mis-tap was permanent: there was no back button and no
           way to change an answer. The pause is only long enough to see the
           selection register. */
        var self = this;
        clearTimeout(this.advance);
        this.advance = setTimeout(function () {
          if (self.at < self.questions.length - 1) self.goTo(self.at + 1);
          else self.finish();
        }, 260);
      }

      back() {
        clearTimeout(this.advance);
        if (this.at > 0) this.goTo(this.at - 1);
        else this.showScreen('intro');
      }

      goTo(i) {
        this.at = i;
        this.questions.forEach(function (q, n) {
          q.hidden = n !== i;
        });
        if (this.stepNow) this.stepNow.textContent = String(i + 1);
        if (this.bar) this.bar.style.width = ((i / this.questions.length) * 100).toFixed(1) + '%';
        if (this.backBtn) this.backBtn.hidden = false;

        var first = this.questions[i].querySelector(FOCUSABLE);
        if (first) first.focus();
      }

      /* ----------------------------------------------------------------------
         scoring
      ---------------------------------------------------------------------- */
      finish() {
        var total = 0;
        var answers = [];
        this.questions.forEach(function (q) {
          total += parseInt(q.dataset.qzAnswer || '0', 10);
          answers.push({
            key: q.dataset.qzQ,
            label: q.dataset.qzAnswerLabel || '',
            score: parseInt(q.dataset.qzAnswer || '0', 10)
          });
        });

        var tier = this.tierFor(total);
        this.dataset.tier = tier;

        /* A visitor coming back from Shopify has just paid for this, so record
           it before anything renders. */
        if (window.location.search.indexOf('customer_posted=true') > -1) this.markUnlocked();

        this.results.forEach(function (r) {
          r.hidden = r.dataset.qzTier !== tier;
        });

        /* The echo block: each answer repeated back with the mechanism behind
           it. The explanation lines are server-rendered per option and carry
           data-qz-because, so nothing is written in JS. */
        var echo = this.querySelector('[data-qz-echo]');
        if (echo) {
          var self = this;
          Array.from(echo.querySelectorAll('[data-qz-echo-for]')).forEach(function (row) {
            var q = self.questions.filter(function (x) {
              return x.dataset.qzQ === row.dataset.qzEchoFor;
            })[0];
            if (!q) return;
            var picked = q.querySelector('[aria-pressed="true"]');
            row.hidden = !picked;
            if (!picked) return;
            var said = row.querySelector('[data-qz-said]');
            var why = row.querySelector('[data-qz-why]');
            if (said) said.textContent = picked.dataset.qzLabel || '';
            if (why) why.textContent = picked.dataset.qzBecause || '';
          });
        }

        if (this.scoreOut) this.scoreOut.textContent = String(total);
        if (this.bar) this.bar.style.width = '100%';

        this.writeFormFields(total, tier);
        this.renderNumbers();
        this.save();
        this.showScreen('results');
      }

      tierFor(total) {
        if (total <= 8) return 'caffeine-curious';
        if (total <= 12) return 'tolerance-builder';
        if (total <= 16) return 'heavy-reliance';
        return 'critical-reset';
      }

      /* THE SCORE GOES WITH THE EMAIL.

         The old quiz sent the tier as a customer tag and threw the score away,
         which is the one number the whole page exists to produce. Both now
         travel: the tier as a tag, and the score in contact[note], the same
         trick sections/newsletter.liquid already uses to attach structured data
         to a Shopify customer record. */
      writeFormFields(total, tier) {
        var name = (this.querySelector('[data-qz-tier="' + tier + '"] [data-qz-tier-name]') || {}).textContent || tier;
        if (this.tagsInput) this.tagsInput.value = 'quiz,' + tier;
        if (this.noteInput) {
          var mg = this.intakeMg();
          this.noteInput.value =
            'Quiz score: ' + total + '/20 · Tier: ' + name.trim() +
            (mg ? ' · Approx intake: ' + mg + 'mg/day' : '');
        }
      }

      /* THE ONE FIGURE THAT IS ACTUALLY THEIRS.

         Question 1 is a band, not a number, so this is the midpoint of the band
         they chose and is labelled "about" everywhere it is shown. It is not
         precise and does not pretend to be; it is enough to say whether the
         programme's opening dose is a step down for them, and by how much. */
      intakeMg() {
        /* restore() re-presses the saved buttons after the form POST, so this
           one selector covers both a fresh run and a return from Shopify. */
        var picked = this.querySelector('[data-qz-mg][aria-pressed="true"]');
        return picked ? parseInt(picked.getAttribute('data-qz-mg'), 10) : 0;
      }

      /* THE NUMBERS CARD, AND THE LOCK ON IT.

         Unlocked by a successful post, and then for good on this device: making
         someone who has already given their address hand it over again on every
         visit would be a worse experience than never gating it.

         OPENING is the programme's first-phase dose. Below it there is nothing
         to bring down, and saying so plainly is the same thing the calculator
         does at its lowest band. Selling a taper to someone who does not need
         one is how a brand loses the person who would have recommended it. */
      renderNumbers() {
        var card = this.querySelector('[data-qz-numbers]');
        var locked = this.querySelector('[data-qz-locked]');
        if (!card || !locked) return;

        var OPENING = 160;
        var mg = this.intakeMg();
        var open = this.isUnlocked();

        card.hidden = !open;
        locked.hidden = open;
        if (!open || !mg) return;

        var cut = mg > OPENING ? Math.round(((mg - OPENING) / mg) * 100) : 0;
        var set = function (sel, val) {
          var el = card.querySelector(sel);
          if (el) el.textContent = val;
        };
        set('[data-qz-num-intake]', 'about ' + mg);
        set('[data-qz-num-day1]', String(OPENING));
        set('[data-qz-num-cut]', cut > 0 ? cut + '%' : 'none');

        var line = card.querySelector('[data-qz-num-line]');
        if (!line) return;
        line.textContent = cut > 0
          ? 'From about ' + mg + 'mg a day, day one of the programme is ' + OPENING +
            'mg: four Reset and two Flow, a ' + cut + ' per cent step down rather than a stop. ' +
            'Reset then drops 40mg every five days until there is no added caffeine left.'
          : 'You are on about ' + mg + 'mg a day, and the programme opens at ' + OPENING +
            'mg. That is above where you already are, so there is nothing here for the kit ' +
            'to bring down. At this level when you drink matters more than how much.';
      }

      /* Takes the reader to the form rather than telling them where it is.
         Focus alone is easy to miss on a long page, so the field is scrolled
         into the middle and flashed for a moment as well. */
      jumpToGate() {
        var field = this.querySelector('.unc-qz__gate input[type="email"]');
        var gate = this.querySelector('.unc-qz__gate');
        if (!gate) return;
        gate.scrollIntoView({
          block: 'center',
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
        });
        if (!field) return;
        var self = this;
        window.setTimeout(function () {
          try { field.focus({ preventScroll: true }); } catch (e) { field.focus(); }
          gate.classList.add('is-calling');
          window.setTimeout(function () { gate.classList.remove('is-calling'); }, 1400);
        }, 320);
      }

      isUnlocked() {
        try {
          if (window.localStorage.getItem('unc-quiz-unlocked') === '1') return true;
        } catch (e) {}
        return window.location.search.indexOf('customer_posted=true') > -1;
      }

      markUnlocked() {
        try {
          window.localStorage.setItem('unc-quiz-unlocked', '1');
        } catch (e) {}
      }

      /* ----------------------------------------------------------------------
         screens
      ---------------------------------------------------------------------- */
      showScreen(which) {
        var self = this;
        Object.keys(this.screens).forEach(function (k) {
          if (self.screens[k]) self.screens[k].hidden = k !== which;
        });
        this.dataset.screen = which;
        if (this.backBtn) this.backBtn.hidden = which !== 'quiz';

        /* Scroll the element into view rather than the page top: the quiz sits
           below a header and the results are long, so jumping to the document
           top would land above the answer they just earned. */
        if (which !== 'intro') {
          var top = this.getBoundingClientRect().top + window.pageYOffset - 90;
          window.scrollTo({ top: top, behavior: 'smooth' });
        }
      }

      reset() {
        clearTimeout(this.advance);
        try {
          window.sessionStorage.removeItem(this.key);
        } catch (e) {}
        this.questions.forEach(function (q) {
          delete q.dataset.qzAnswer;
          delete q.dataset.qzAnswerLabel;
          Array.from(q.querySelectorAll('[data-qz-score-value]')).forEach(function (b) {
            b.setAttribute('aria-pressed', 'false');
          });
        });
        delete this.dataset.tier;
        if (this.bar) this.bar.style.width = '0%';
        this.showScreen('intro');
      }
    }
  );
})();
